import React, { useEffect, useState } from "react";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import { storeStateType, useAppDispatch } from "../../redux/store";
import Button from "@mui/material/Button";
import moment from "moment";

import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { LOADING_EVENT_START } from "../../redux/types/schedule.types";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import {
  filterEventsType,
  loadEmployees,
} from "../../redux/actions/schedule.actions";

import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import { CSVLink } from "react-csv";
import axiosInstance from "../../api";
import { alertActions } from "../../redux/actions/alert.actions";
import { IFacilityOption } from "../CalendarForms/Types/calendarTypes";
import FacilityInputField from "../CalendarForms/InputsComponents/FacilityInputField";
import DateInput from "../CalendarForms/InputsComponents/DateInput";

type HeaderWeeklyCalendarPropsType = {
  setSelectedFacility: React.Dispatch<
    React.SetStateAction<IFacilityOption | null>
  >;
  setOverallDate: React.Dispatch<React.SetStateAction<string>>;
  setFilterByWorktype: React.Dispatch<React.SetStateAction<string[]>>;
  setFilterEvents: React.Dispatch<React.SetStateAction<filterEventsType>>;
  setCalendarType: React.Dispatch<React.SetStateAction<"weekly" | "monthly">>;
  calendarType: "weekly" | "monthly";
  overallDate: string;
  filterEvents: filterEventsType;
};

export type searchOptionListType = { title: string; value: string };
export type worktypeOptionType = { id: string; name: string };

export default function HeaderWeeklyCalendar(
  props: HeaderWeeklyCalendarPropsType
) {
  const {
    setSelectedFacility,
    setOverallDate,
    setFilterByWorktype,
    setFilterEvents,
    setCalendarType,
    calendarType,
    overallDate,
    filterEvents,
  } = props;
  const [employeeSearchOptions, setSearchOptions] = useState<
    { id: string; name: string; key: string }[]
  >([]);

  const dispatch = useAppDispatch();

  const employees = useSelector(
    (state: storeStateType) => state.schedule.employees
  );
  const { work_types } = useSelector((state: storeStateType) => state.schedule);

  // for csvlink
  const [csvData, setCsvData] = React.useState([]);
  const [csvLoading, setCsvLoading] = React.useState<boolean>(false);

  const [, setSelectedEmployees] = useState<string[]>([]);

  const {
    register,
    setValue,
    watch,
    getValues,
    control,
    formState: { errors },
  } = useForm<any>({ defaultValues: { facility: null, selectDate: "" } });

  let selectedFacility = watch("facility");
  let selectedDate = watch("selectDate");

  useEffect(() => {
    dispatch(loadEmployees());
  }, [dispatch]);

  useEffect(() => {
    setSelectedFacility(getValues("facility"));
    dispatch({ type: LOADING_EVENT_START });
  }, [selectedFacility, getValues, setSelectedFacility, dispatch]);

  useEffect(() => {
    setOverallDate(getValues("selectDate"));
  }, [selectedDate, getValues, setOverallDate]);

  useEffect(() => {
    let options = employees?.map((employee) => {
      return {
        name: `${employee?.fullname}`,
        id: employee.id,
        key: employee.id,
      };
    });
    setSearchOptions(options ?? []);
  }, [employees]);

  // const memoizedHandleSearch = useMemo(() => {
  //   return (searchText: null | string) => {
  //     if (searchText !== null) {
  //       dispatch({ type: LOADING_EVENT_START });

  //       setFilterEvents((preState: filterEventsType) => {
  //         return { ...preState, employee: searchText };
  //       });
  //     }
  //   };
  // }, [setFilterEvents, dispatch]);

  function handleWorkType(data: any, selectedValue: worktypeOptionType[]) {
    dispatch({ type: LOADING_EVENT_START });

    let selectedList = selectedValue.map(
      (option: worktypeOptionType) => option.id
    );
    setFilterByWorktype(selectedList);
    setFilterEvents((preState: filterEventsType) => {
      return { ...preState, worktype: selectedList };
    });
  }

  function handleEmployeeSearch(
    data: any,
    selectedValue: worktypeOptionType[]
  ) {
    dispatch({ type: LOADING_EVENT_START });

    let selectedList = selectedValue.map(
      (option: worktypeOptionType) => option.id
    );
    setSelectedEmployees(selectedList);
    setFilterEvents((preState: filterEventsType) => {
      return { ...preState, employee: selectedList };
    });
  }
  function handlePreviousWeekBtn() {
    dispatch({ type: LOADING_EVENT_START });
    setOverallDate((preState: string) => {
      let momentObj = moment(preState);
      if (calendarType === "weekly") {
        return momentObj.subtract(7, "days").format("YYYY-MM-DD");
      } else {
        return momentObj.subtract(1, "months").format("YYYY-MM-DD");
      }
    });
  }
  function handleNextWeekBtn() {
    dispatch({ type: LOADING_EVENT_START });
    setOverallDate((preState: string) => {
      let momentObj = moment(preState);
      if (calendarType === "weekly") {
        return momentObj.add(7, "days").format("YYYY-MM-DD");
      } else return momentObj.add(1, "months").format("YYYY-MM-DD");
    });
  }

  function handleCalendarChange() {
    dispatch({ type: LOADING_EVENT_START });
    setCalendarType((preState) => {
      if (preState === "weekly") {
        return "monthly";
      } else {
        return "weekly";
      }
    });
  }

  useEffect(() => {
    if (csvData.length) {
      csvDownloadBtn.current.link.click();
    }
  }, [csvData]);

  const compressData = () => {
    setCsvLoading(true);

    axiosInstance
      .get("/schedule/csv", { params: filterEvents })
      .then((res) => {
        setCsvData(res.data);
        setCsvLoading(false);
      })
      .catch((err) => {
        dispatch(alertActions.showError(err, "Error getting csv"));
        setCsvLoading(false);
      });
  };
  const csvDownloadBtn = React.useRef<any>();

  const csvHeaders = [
    { label: `Schedule`, key: "id" },
    {
      label: `"Schedule" Date`,
      key: "schedule_date",
    },
    {
      label: ` "Worktype"`,

      key: "work_type",
    },
    { label: "Start time", key: "start_time" },
    { label: "Status", key: "schedule_status" },
    { label: "Notes", key: "schedule_note" },
    { label: "Assigned to", key: "employee_name" },
    { label: "End time", key: "end_time" },
    { label: "Facility", key: "facility" },
    { label: "Sub facility", key: "sub_facility" },
    { label: "Created At", key: "created_at" },
    // { label: "Created By Id", key: "created_by" },
  ];

  return (
    <Box
      className="weekly-calendar-header"
      sx={{ backgroundColor: "background.paper" }}
    >
      <Grid container columnSpacing={2} alignItems={"center"} margin={0}>
        <Grid item xs={1}>
          <>
            <Box
              display="none"
              ref={csvDownloadBtn}
              component={CSVLink}
              className="text_decoration_none"
              data={csvData}
              headers={csvHeaders}
              filename={"schedule"}
            ></Box>
            <Button
              startIcon={<SaveAltOutlinedIcon />}
              size="small"
              onClick={compressData}
              disabled={csvLoading ? true : false}
              sx={{ m: 0, p: 0 }}
            >
              {/* <Hidden smDown>{csvLoading ? "Loading csv..." : "Export"}</Hidden> */}
            </Button>
          </>
        </Grid>
        <Grid item xs={3}>
          <FacilityInputField
            className="schedule-header-facility"
            label={`Select facility`}
            variableName="facility"
            control={control}
            errors={errors}
            showParentOnly={true}
            setValue={setValue}
            isLoadFacilities={true}
          />
        </Grid>
        <Grid item xs={3} p={1} display="flex" alignItems="center">
          <Button
            variant="contained"
            size="large"
            color="primary"
            onClick={handlePreviousWeekBtn}
            sx={{
              marginRight: "10px",
              minWidth: "38px",
              width: "38px",
            }}
          >
            <ArrowLeftIcon />
          </Button>
          {/* 
          <CustomDatePicker
            variableName="selectDate"
            defaultValue={overallDate}
            register={register}
            errors={errors}
            setValue={setValue}
            disableOutline={true}
            control={control}
          />
           */}
          <DateInput
            variableName="selectDate"
            label="Select Date"
            defaultValue={overallDate}
            register={register}
            errors={errors}
            setValue={setValue}
            disableUnderline={true}
            sx={{ maxWidth: "150px" }}
          />

          <Button
            size="large"
            color="primary"
            variant="contained"
            onClick={handleNextWeekBtn}
            sx={{
              marginLeft: "10px",
              minWidth: "38px",
              width: "38px",
            }}
          >
            <ArrowRightIcon />
          </Button>
        </Grid>

        <Grid item xs={2} ml="auto">
          <Box component={"div"} width="100%">
            <Autocomplete
              multiple
              id="tags-standard"
              options={work_types}
              getOptionLabel={(option: worktypeOptionType) => option.name}
              defaultValue={[]}
              renderInput={(params) => (
                <TextField
                  className="filter-work-type"
                  {...params}
                  variant="standard"
                  label={`Worktype`}
                />
              )}
              onChange={handleWorkType}
            />
          </Box>
        </Grid>
        <Grid item xs={2}>
          <Box component={"div"} width="100%">
            <Autocomplete
              multiple
              id="tags-standard-employee"
              options={employeeSearchOptions}
              getOptionLabel={(option: worktypeOptionType) =>
                `${option.name}(${option.id})`
              }
              defaultValue={[]}
              renderInput={(params) => (
                <TextField
                  className="filter-work-type"
                  {...params}
                  variant="standard"
                  label={`Employee`}
                />
              )}
              onChange={handleEmployeeSearch}
            />
          </Box>
          {/* <SearchBar
            placeholder="Search employee"
            handleSearch={memoizedHandleSearch}
            options={employeeSearchOptions}
            className="disable-underline"
          /> */}
        </Grid>

        <Grid item xs={1} display="flex" alignItems="flex-end">
          <Button
            onClick={handleCalendarChange}
            sx={{ color: "text.secondary" }}
            title="Calendar Type"
          >
            {calendarType === "monthly" ? (
              <CalendarMonthOutlinedIcon />
            ) : (
              <CalendarViewMonthOutlinedIcon />
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
